import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { setOrderToGenerate } from 'src/app/redux/actions/order.action';
import { isBuyerOfGrassiGroup, StructBuyerProform, isValidOrderByBuyer } from '@shared-logic/subjects';
import { PLANTAS_INTERIOR_MAP, getPlaceOfDeliveryIBM, puertos } from '@shared-logic/locations';
import { appState } from 'src/app/shared/interfaces/appState.interface';
import { Order } from '@shared-logic/orders';
import { resetAllOrders } from 'src/app/shared/order';
import { getPortIBMStr, getQualityStr } from 'src/app/shared/price';
import { getArrowDirectionByBool } from 'src/app/shared/validator.checker';

export const ACT_PROFORM = {
  AUTOCORRECT: 'autocorrect',
  IGNORE: 'ignore'
}

interface Invalid {
  quality: boolean;
  place: boolean;
  wayPay: boolean;
}

@Component({
  selector: 'app-form-proform-buyer',
  templateUrl: './form-proform-buyer.component.html',
  styleUrls: ['./form-proform-buyer.component.scss']
})
export class FormProformBuyerComponent implements OnInit, OnDestroy, OnChanges {
  public IGNORE_CONST = ACT_PROFORM.IGNORE;
  @Input() order!: Order;
  @Input() orderOriginal!: Order | null;
  @Input() proform!: StructBuyerProform | null;
  @Input() buyer!: number;
  @Input() public actionProform: string = '';

  @Output() action = new EventEmitter<{ action: string, newOrder: Order | null }>();
  @Output() actualizate = new EventEmitter<{ newOrder: Order | null }>();
  @Output() actualizatePlaceOfDelivery = new EventEmitter<{ newOrder: Order | null }>();

  showInfoToComplete: boolean = false;

  qualityToSave: number = 0;
  placeToSave: number = 0;
  percentageWayPayToSave: number = 0;

  PLACES_MAP: any = {};

  quality$!: Subscription;
  wayPay$!: Subscription;
  placeOfDelivery$!: Subscription;
  constructor(private store: Store<appState>, private cdr: ChangeDetectorRef) {
    this.PLACES_MAP = PLANTAS_INTERIOR_MAP;

    this.quality$ = this.store.select('order', 'generateOrder', 'price', 'quality').subscribe((quality) => {
      if (quality != this.qualityToSave) {
        this.actualizateQuality(quality);
      }
    });

    this.wayPay$ = this.store.select('order', 'generateOrder', 'price', 'wayPay').subscribe((wayPay) => {
      if (wayPay.percentage != this.percentageWayPayToSave) {
        this.actualizateWayPayPercentage(wayPay.percentage);
      }
    });

    this.placeOfDelivery$ = this.store.select('order', 'generateOrder', 'price', 'placeOfDelivery').subscribe((placeOfDelivery) => {
      
      if (!this.order || !placeOfDelivery) {
        return;
      }

      const currentPort = this.order.price.placeOfDelivery?.port ?? 0;
      const currentZone = this.order.price.placeOfDelivery?.zone ?? 0;
      const nextPort = placeOfDelivery.port ?? 0;
      const nextZone = placeOfDelivery.zone ?? 0;

      if (currentPort == nextPort && currentZone == nextZone) {
        return;
      }

      const newOrder = {
        ...this.order,
        price: {
          ...this.order.price,
          placeOfDelivery: {
            zone: nextZone,
            port: nextPort,
          }
        }
      };

      this.order = newOrder;
      this.actualizatePlaceOfDelivery.emit({ newOrder });
    });
  }

  ngOnInit(): void {
    this.setAllValues(this.order)

    this.updateShowInfo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['proform'] || changes['buyer']) {
      this.updateShowInfo();
    }
  }

  updateShowInfo() {
    this.showInfoToComplete = !isAllValidInProform(this.proform) && (this.proform ? true : false);
  }

  ngOnDestroy(): void {
    this.quality$.unsubscribe();
    this.wayPay$.unsubscribe();
    this.placeOfDelivery$.unsubscribe();

    resetAllOrders(this.store);
  }

  getIfTheyAreCorrectFields(): boolean {
    return this.proform != null && isAllValidInProform(this.proform);
  }

  noParametrizationFound(): boolean {
    return !this.hasUsableProform() && !this.isBuyerOfGrassiGroup() && this.buyer > 0;
  }

  hasUsableProform(): boolean {
    if (!this.proform) {
      return false;
    }

    const fields = [
      this.proform.product,
      this.proform.quality,
      this.proform.place,
      this.proform.wayPay,
    ];

    const isStructVoid = fields.every(field =>
      !field.isValid &&
      field.default <= 0 &&
      field.arrayValids.length == 0
    );

    return !isStructVoid;
  }

  getIfTheDataMatches(): boolean {
    return (this.proform ? true : false) && !isAllValidInProform(this.proform);
  }

  setAllValues(order: Order) {
    this.store.dispatch(setOrderToGenerate({ order: order }));
    this.placeToSave = getPlaceOfDeliveryIBM(order);
  }

  getPercentageWayPayBuyer() {
    return this.proform?.wayPay.default;
  }

  getQualityBuyer() {
    return getQualityStr(this.proform?.quality.default ?? 0);
  }

  getPlaceOfDeliveryBuyer() {
    return this.getPlaceOfDeliveryStr(this.proform?.place.default ?? 0);
  }

  getPlaceOfDeliveryStr(code: number) {
    return getPortIBMStr(code ?? 0);
  }

  setPlaceOfDelivery(value: any) {
    this.actualizatePlaceOfDeliveryFromProform(value.element);
  }

  getArrowDirection() {
    return getArrowDirectionByBool(this.showInfoToComplete);
  }

  changeShowInfo() {
    this.showInfoToComplete = !this.showInfoToComplete;
  }

  actualizateQuality(quality: number) {
    this.qualityToSave = quality;
    if (this.order) {
      const newOrder = { ...this.order, price: { ...this.order.price, quality: quality } };
      this.actualizateDataProform(newOrder);
    }
  }

  actualizateWayPayPercentage(percentage: number) {
    this.percentageWayPayToSave = percentage;
    if (this.order) {
      const newOrder = { ...this.order, price: { ...this.order.price, wayPay: { ...this.order.price.wayPay, percentage: percentage } } };
      this.actualizateDataProform(newOrder);
    }
  }

  actualizatePlaceOfDeliveryFromProform(value: number) {
    this.placeToSave = value;
    if (this.order) {
      const newOrder = { ...this.order, price: { ...this.order.price, placeOfDelivery: { zone: 11, port: this.getPort(value) } } };
      this.actualizateDataProform(newOrder);
    }
  }

  actualizateDataProform(newOrder: Order) {
    this.proform = isValidOrderByBuyer(newOrder, this.buyer);
    //this.arrayPlaces=this.proform?.place.arrayValids ?? [];
    this.order = newOrder;
    this.actualizate.emit({ newOrder: newOrder })
  }

  getPort(portIBM: number) {
    return puertos[11].dropdown.indexOf(this.getPlaceOfDeliveryStr(portIBM)) + 1
  }

  autocorrect() {
    //Obtenemos los datos vacíos de la orden
    const newOrder = this.getOrderBy(this.order,
      this.proform?.quality.default ?? -1,
      this.getPort(this.proform?.place?.default ?? 0),
      this.proform?.wayPay.default ?? -1);

    if (newOrder) {
      //Seteamos la nueva proforma, orden, plazas, si están activos, etc.
      this.actualizateDataProform(newOrder);

      //Seteamos los valores de plaza, calidad y porcentaje de pago
      this.setAllValues(newOrder);
    }
  }

  getOrderBy(order: Order, quality: number, place: number, percentage: number) {
    return order ? {
      ...order,
      price: {
        ...order.price,
        placeOfDelivery: {
          zone: 11, //Plantas interior
          port: place,
        },
        quality: quality,
        wayPay: {
          ...order.price.wayPay,
          percentage: percentage
        }
      }
    } : null
  }

  ignore() {
    this.action.emit({ action: ACT_PROFORM.IGNORE, newOrder: this.orderOriginal });

    this.showInfoToComplete = false;
    if (this.orderOriginal) {
      this.order = this.orderOriginal;
      //this.actualizateDataProform(this.orderOriginal);
    }
  }

  confirm() {
    this.action.emit({ action: ACT_PROFORM.AUTOCORRECT, newOrder: this.order });
    this.showInfoToComplete = false;
  }

  isBuyerOfGrassiGroup() {
    return isBuyerOfGrassiGroup(this.buyer);
  }

  getPlaceOfDeliveryIBM(order: Order) {
    return getPlaceOfDeliveryIBM(order);
  }

}

/**
 * Given a proforma, returns true in case all its isValid fields are true.
 * @param proform
 * @returns boolean
 */
export function isAllValidInProform(proform: StructBuyerProform | null): boolean {
  return proform ? proform.quality.isValid && proform.place.isValid && proform.wayPay.isValid : false;
}